<template>
  <div class="pt-3">
    <div class="font-bold mb-4">{{ t("common.general") }}</div>
    <v-text-field
      v-model.trim="accountName"
      :label="t('account.settings.accountName')"
      :rules="[required]"
    ></v-text-field>
    <SelectGroup v-model="groupId" v-model:new-group-name="newGroupName"></SelectGroup>
    <v-radio-group
      v-model="newOperatorLanguage"
      color="primary"
      :label="t('account.operatorLanguage')"
      inline
      class="mt-2"
    >
      <v-radio
        v-for="lang in languages"
        :key="lang.code"
        :label="t(`locales.${lang.code}`)"
        :value="lang.code"
      />
    </v-radio-group>

    <AdminOnlyCard v-if="account?.accountType === 'powens'" class="mt-4">
      <div class="font-bold mb-2">{{ t("account.settings.powensAdmin.title") }}</div>
      <p class="text-sm text-medium-emphasis mb-3">
        {{ t("account.settings.powensAdmin.syncHint") }}
      </p>
      <v-btn
        color="primary"
        variant="outlined"
        :loading="syncingPowens"
        :disabled="syncingPowens || !account?.powens_connection?.id"
        append-icon="mdi-sync"
        @click="handlePowensSync()"
      >
        {{ t("account.settings.powensAdmin.syncConnection") }}
      </v-btn>
    </AdminOnlyCard>

    <template v-if="account && getProviderType(account.accountType) === 'proAccount'">
      <ContractSettings
        :account="account"
        :supervisor-org="supervisorOrg"
        :loading="loading"
        :contract-type="contractType"
        :contract-template-id="contractTemplateId"
        :contract-custom-file="contractCustomFileInput"
        :signature-a-p-i-statuses="signatureAPIStatuses"
        :max-contract-file-size-m-b="maxContractFileSizeMB"
        :deleting-contract="deletingContract"
        :contract-templates="contractTemplates"
        @update:contract-type="contractType = $event"
        @update:contract-template-id="contractTemplateId = $event"
        @update:contract-custom-file="contractCustomFileInput = $event"
        @delete-contract-custom="handleDeleteContractCustom"
        @download-contract="downloadContract"
      />

      <div class="font-bold mt-5 mb-3">{{ t("account.settings.pricing") }}</div>
      <v-text-field
        :model-value="account?.proAccountPrice?.toFixed(2)"
        disabled
        readonly
        :label="t('account.settings.proAccountPrice')"
        hide-details="auto"
      >
        <template #prepend-inner>
          <v-icon>mdi-currency-eur</v-icon>
        </template>
      </v-text-field>
      <AdminOnlyCard class="mt-3">
        <v-select
          v-model="editableProAccountPrice"
          :items="proAccountPriceOptionsItems"
          :label="t('account.settings.proAccountPrice')"
          prepend-inner-icon="mdi-currency-eur"
          hide-details="auto"
        ></v-select>
        <ProAccountPricingTiersHint :show="hasMultipleProAccountPrices" />

        <StripeSubscriptionSettings :account="account" :account-id="accountId" />
      </AdminOnlyCard>

      <div class="font-bold mt-5">{{ t("account.settings.transfers") }}</div>
      <v-radio-group
        v-model="limitTransfers"
        :disabled="!user.isKind('admin') || loading"
        hide-details
        color="primary"
      >
        <v-radio :label="t('addAccountDialog.acceptAllTransfers')" value="unlimited"></v-radio>
        <v-radio :label="t('addAccountDialog.requireValidation')" value="all"></v-radio>
        <v-radio :label="t('addAccountDialog.requireValidationAbove')" value="threshold"></v-radio>
      </v-radio-group>
      <div v-if="!user.isKind('admin')" class="text-medium-emphasis text-xs mt-1">
        {{ t("account.settings.contactUsToModifyTransferSettings") }}
      </div>

      <template v-if="limitTransfers === 'threshold'">
        <v-text-field
          v-model.number="transferAmountThreshold"
          type="number"
          min="0"
          :rules="user.isKind('admin') ? [required, positiveNumber] : undefined"
          :disabled="!user.isKind('admin') || loading"
          class="mt-2"
          :label="t('addAccountDialog.transferValidationThreshold')"
          prepend-inner-icon="mdi-currency-eur"
          hide-details="auto"
        />
      </template>
    </template>

    <template v-if="account?.accountType === 'swan'">
      <div class="font-bold mt-5">{{ t("account.settings.permissions") }}</div>
      <v-switch
        v-model="canUseSwanCards"
        color="primary"
        :label="t('account.settings.canUseSwanCards')"
        hide-details
        class="mt-2"
      ></v-switch>
    </template>

    <div class="flex items-center mb-5 mt-8">
      <div>
        <div class="font-bold">{{ t("account.settings.contacts") }}</div>
        <div class="text-medium-emphasis text-sm">
          {{
            account && getProviderType(account.accountType) === "proAccount"
              ? t("account.settings.contactsAtYourAccount")
              : t("account.settings.contactsAtYourOperator")
          }}
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn append-icon="mdi-plus" border @click="openAddDialog()">{{ t("common.add") }}</v-btn>
    </div>

    <v-table density="compact" class="mt-4">
      <thead>
        <tr>
          <th class="text-left">{{ t("common.email") }}</th>
          <th class="text-left">{{ t("common.phone") }}</th>
          <th
            v-if="account && getProviderType(account.accountType) === 'openBanking'"
            class="text-left"
          >
            {{ t("account.settings.lastNotifiedAt") }}
          </th>
          <th class="text-left">{{ t("common.actions") }}</th>
        </tr>
      </thead>
      <v-skeleton-loader v-if="loadingContacts" type="table-thead, table-tbody"></v-skeleton-loader>
      <tbody>
        <tr v-for="contact in contacts" :key="contact.id">
          <td>
            {{ contact.email }}
          </td>
          <td>
            {{ contact.phone || t("common.none") }}
          </td>
          <td v-if="account && getProviderType(account.accountType) === 'openBanking'">
            {{ contact.lastNotifiedAt ? getRelativeTime(contact.lastNotifiedAt.toDate()) : "-" }}
            <v-chip
              v-if="contact.notificationsDisabled"
              v-tooltip="t('account.settings.notificationsDisabledTooltip')"
              append-icon="mdi-information-outline"
              color="default"
              size="small"
            >
              {{ t("account.settings.notificationsDisabled") }}
            </v-chip>
          </td>
          <td>
            <v-menu>
              <template #activator="{ props: _props }">
                <v-btn icon="mdi-dots-vertical" density="compact" v-bind="_props"> </v-btn>
              </template>
              <v-list density="compact">
                <v-list-item
                  v-if="account && getProviderType(account.accountType) === 'openBanking'"
                  @click="sendLink(contact)"
                >
                  <v-list-item-title>{{ t("account.settings.sendSyncLink") }}</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="
                    account &&
                    getProviderType(account?.accountType) === 'openBanking' &&
                    contact.notificationsDisabled &&
                    user.isKind('admin')
                  "
                  @click="handleActiveNotifications(contact)"
                >
                  <v-list-item-title>{{
                    t("account.settings.activeNotifications")
                  }}</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="user.isKind('admin')" @click="openEmailHistory(contact)">
                  <v-list-item-title>{{
                    t("account.settings.emailHistoryAdmin")
                  }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="editContact(contact)">
                  <v-list-item-title>{{ t("common.edit") }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="removeContact(contact)">
                  <v-list-item-title>{{ t("common.delete") }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
        <tr v-if="!contacts.length">
          <td colspan="999" class="text-center">{{ t("account.settings.noContacts") }}</td>
        </tr>
      </tbody>
    </v-table>
    <Collaborators
      v-model="selectedSupervisorIds"
      v-model:mode="assignedSupervisorsMode"
      :users="users"
      :loading="loadingUsers"
    />
    <ExternalApproverAssignments v-model="externalApproverAssignments" :loading="loadingUsers" />
    <template v-if="account && getProviderType(account.accountType) === 'proAccount'">
      <div class="mb-5 mt-8">
        <div class="font-bold">{{ t("notifications.title") }}</div>
        <div class="text-medium-emphasis text-sm">
          {{ t("notifications.sendEmailWhen") }}
        </div>
      </div>
      <div class="pt-3" style="max-width: 400px">
        <v-text-field
          v-model.number="notifications.balance.max"
          clearable
          type="number"
          :label="t('notifications.balanceAbove')"
        >
          <template #append-inner>{{ currencySymbol }}</template>
        </v-text-field>
        <v-text-field
          v-model.number="notifications.balance.min"
          clearable
          type="number"
          :label="t('notifications.balanceBelow')"
        >
          <template #append-inner>{{ currencySymbol }}</template>
        </v-text-field>
        <v-text-field
          v-model.number="notifications.transaction.max"
          clearable
          type="number"
          :label="t('notifications.transactionAbove')"
        >
          <template #append-inner>{{ currencySymbol }}</template>
        </v-text-field>
        <v-select
          :model-value="notifications.inactivity.enabled ? notifications.inactivity.days : 0"
          :items="inactivityItems"
          :label="t('notifications.inactivity')"
          @update:model-value="
            (days: number) =>
              (notifications.inactivity = days
                ? { enabled: true, days }
                : { ...notifications.inactivity, enabled: false })
          "
        ></v-select>
      </div>
    </template>
    <v-btn :loading="loading" :disabled="loading" class="mt-8" color="success" @click="save()">{{
      t("common.save")
    }}</v-btn>
    <v-btn
      v-if="!account?.isArchived"
      class="mt-4 flex"
      :loading="loading"
      :disabled="loading"
      variant="outlined"
      append-icon="mdi-archive"
      color="warning"
      @click="toggleArchived()"
      >{{ t("account.settings.archiveAccount") }}</v-btn
    >
    <v-btn
      v-else-if="account?.isArchived"
      class="mt-4 flex"
      :loading="loading"
      :disabled="loading"
      variant="outlined"
      color="primary"
      @click="toggleArchived()"
      >{{ t("account.settings.reactivateAccount") }}</v-btn
    >
    <AdminOnlyCard class="mt-4 inline-block">
      <div class="flex flex-col items-start gap-2">
        <template v-if="allowTreezorSimulation && account?.accountType === 'treezor'">
          <v-btn
            v-if="account.treezorCompany?.kyc?.kycStatus !== 'VALIDATED'"
            :loading="simulatingTreezorKYBFlow"
            :disabled="simulatingTreezorKYBFlow"
            color="primary"
            variant="outlined"
            append-icon="mdi-bank-check"
            @click="simulateTreezorKYBFlow()"
          >
            {{ t("account.settings.simulateTreezorKYBFlow") }}
          </v-btn>
          <v-btn
            :loading="creditingTreezorWallet"
            :disabled="creditingTreezorWallet"
            color="primary"
            variant="outlined"
            append-icon="mdi-cash-plus"
            @click="simulateTreezorCreditWallet()"
          >
            {{ t("account.settings.simulateTreezorCreditWallet") }}
          </v-btn>
        </template>
        <v-btn
          v-if="account?.accountType === 'treezor'"
          :loading="syncingTreezor"
          :disabled="syncingTreezor"
          color="primary"
          variant="outlined"
          append-icon="mdi-sync"
          @click="syncTreezorAccount()"
        >
          {{ t("account.settings.syncTreezorAccount") }}
        </v-btn>
        <v-btn
          :loading="deletingAccount"
          :disabled="deletingAccount"
          variant="outlined"
          append-icon="mdi-delete"
          color="error"
          data-testid="settings-delete-account-button"
          @click="deleteAccountHandler()"
          >{{ t("account.settings.deleteAccount") }}</v-btn
        >
      </div>
    </AdminOnlyCard>
    <ContactEditDialog
      v-model="showContactDialog"
      :contact="editingContact"
      :contacts="contacts"
      :loading="contactActionLoading"
      @update:contact="handleContactUpdate"
      @add:contact="handleContactAdd"
    />
  </div>
</template>

<script lang="ts" setup>
import AdminOnlyCard from "@/components/AdminOnlyCard.vue";
import ContactEditDialog from "@/components/ContactEditDialog.vue";
import ProAccountPricingTiersHint from "@/components/ProAccountPricingTiersHint.vue";
import SelectGroup from "@/components/SelectGroup.vue";
import { useAccountCurrency } from "@/composables/useAccountCurrency.ts";
import { useContract } from "@/composables/useContract";
import { useCollection, useDocument } from "@/composables/useFirestore";
import { useGroup } from "@/composables/useGroup";
import { useI18n } from "@/composables/useI18n";
import { useLanguage } from "@/composables/useLanguage";
import { useRelativeTime } from "@/composables/useRelativeTime";
import { useRules } from "@/composables/useRules";
import { useSupervisorOrg } from "@/composables/useSupervisorOrg";
import { call, db } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import {
  contactsFullPath,
  getProviderType,
  type Account,
  type AssignedSupervisors,
  type Contact,
} from "@aircap/common/account";
import { logsFullPath, type Log } from "@aircap/common/log";
import type { Locale } from "@aircap/common/models";
import {
  contractTemplatesFullPath,
  supervisorOrgUsersFullPath,
  type ContractTemplate,
  type SupervisorOrgUser,
} from "@aircap/common/supervisorOrg";
import { captureException } from "@sentry/vue";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { computed, ref, toRef, watch } from "vue";
import { useRouter } from "vue-router";
import Collaborators from "./components/Collaborators.vue";
import ContractSettings from "./components/ContractSettings.vue";
import ExternalApproverAssignments from "./components/ExternalApproverAssignments.vue";
import StripeSubscriptionSettings from "./components/StripeSubscriptionSettings.vue";

type ContactWithId = Contact & { id: string };

const props = defineProps<{
  accountId: string;
}>();

const router = useRouter();
const user = useUserStore();
const { t } = useI18n();
const { languages } = useLanguage();
const { currencySymbol } = useAccountCurrency(toRef(props, "accountId"));
const { saveGroup, newGroupName } = useGroup();
const { showSuccessToast, showErrorToast } = useToast();
const { required, positiveNumber } = useRules();
const { getRelativeTime } = useRelativeTime();

const newOperatorLanguage = ref<Locale | null>(null);
const transferAmountThreshold = ref<number | null>(null);
const limitTransfers = ref<"unlimited" | "all" | "threshold">("unlimited");
const groupId = ref<string | null>(null);
const accountName = ref<string | null>(null);
const loading = ref(false);
const selectedSupervisorIds = ref<string[]>([]);
const assignedSupervisorsMode = ref<AssignedSupervisors["mode"]>("all");
const externalApproverAssignments = ref<string[]>([]);
const canUseSwanCards = ref(false);
const editableProAccountPrice = ref<number | null>(null);
const deletingAccount = ref(false);
const syncingPowens = ref(false);
const syncingTreezor = ref(false);
const simulatingTreezorKYBFlow = ref(false);
const creditingTreezorWallet = ref(false);

const allowTreezorSimulation = import.meta.env.VITE_TREEZOR_ALLOW_SIMULATION === "true";

type notifs = {
  balance: {
    min: number | null;
    max: number | null;
  };
  transaction: {
    min?: number | null;
    max: number | null;
  };
  inactivity: {
    enabled: boolean;
    days: number;
  };
};

const notifications = ref<notifs>({
  balance: {
    min: null,
    max: null,
  },
  transaction: {
    min: null,
    max: null,
  },
  inactivity: {
    enabled: false,
    days: 30,
  },
});

const inactivityItems = computed(() => [
  { title: t("notifications.inactivityNone"), value: 0 },
  ...[...new Set([30, 90, 180, notifications.value.inactivity.days])]
    .sort((a, b) => a - b)
    .map((days) => ({ title: t("notifications.inactivityDays", { days }), value: days })),
]);

const usersRef = computed(() =>
  user.supervisorOrgId
    ? query(
        collection<SupervisorOrgUser>(db, supervisorOrgUsersFullPath(user.supervisorOrgId)),
        where<SupervisorOrgUser>("role", "in", ["supervisor", "nonApprovingSupervisor"]),
      )
    : null,
);
const { data: users, pending: loadingUsers } = useCollection<SupervisorOrgUser>(usersRef);

const accountRef = computed(() => doc(collection<Account>(db, "accounts"), props.accountId));
const account = useDocument<Account>(accountRef);

const { supervisorOrg } = useSupervisorOrg();

const contractTemplatesRef = computed(() =>
  user.supervisorOrgId
    ? query(
        collection<ContractTemplate>(db, contractTemplatesFullPath(user.supervisorOrgId)),
        orderBy<ContractTemplate>("createdAt", "asc"),
      )
    : null,
);
const { data: contractTemplates } = useCollection<ContractTemplate>(contractTemplatesRef);

const contractTemplateId = ref<string | null | undefined>(undefined);

const proAccountPriceOptionsItems = computed(
  () => supervisorOrg.value?.proAccountPriceOptions ?? [],
);
const hasMultipleProAccountPrices = computed(() => proAccountPriceOptionsItems.value.length > 1);

const showContactDialog = ref(false);
const editingContact = ref<ContactWithId | null>(null);
const contactActionLoading = ref(false);

const {
  contractType,
  contractCustomFileInput,
  deletingContract,
  signatureAPIStatuses,
  maxContractFileSizeMB,
  downloadContract,
  handleDeleteContractCustom,
  uploadContractCustom,
  validateContractCustomFileInput,
  initializeFromAccount,
} = useContract(props.accountId, account);

watch(
  account,
  (account) => {
    if (account) {
      groupId.value = account.groupId;
      accountName.value = account.name;
      const threshold = account.transferAmountThreshold;
      if (threshold === undefined || threshold === null) {
        limitTransfers.value = "unlimited";
        transferAmountThreshold.value = null;
      } else if (threshold === 0) {
        limitTransfers.value = "all";
        transferAmountThreshold.value = null;
      } else {
        limitTransfers.value = "threshold";
        transferAmountThreshold.value = threshold;
      }
      assignedSupervisorsMode.value = account.assignedSupervisors.mode;
      selectedSupervisorIds.value =
        account.assignedSupervisors.mode === "selected"
          ? [...account.assignedSupervisors.uids]
          : [];
      externalApproverAssignments.value = account.externalApprovers ?? [];
      newOperatorLanguage.value = account.operatorLanguage;
      canUseSwanCards.value = account.canUseSwanCards ?? false;
      editableProAccountPrice.value = account.proAccountPrice ?? null;
      if (getProviderType(account.accountType) === "proAccount") {
        notifications.value = {
          balance: { min: null, max: null },
          transaction: { min: null, max: null },
          ...account.notifications,
          inactivity: { enabled: false, days: 30, ...account.notifications?.inactivity },
        };
        contractTemplateId.value = account.contractTemplateId ?? null;
        initializeFromAccount(account);
      }
    }
  },
  { immediate: true },
);

const contactsSource = computed(() => collection<Contact>(db, contactsFullPath(props.accountId)));

const { data: contacts, pending: loadingContacts } = useCollection<Contact>(contactsSource);

const toggleArchived = async () => {
  if (!account.value) return;

  loading.value = true;
  const isArchived = account.value.isArchived;

  try {
    console.log("Toggling project archived state...");
    const accountRef = doc<Account>(db, `accounts/${props.accountId}`);
    await updateDoc(accountRef, { isArchived: !isArchived });
    showSuccessToast(t("account.settings.saveSuccess"));
  } catch (cause) {
    const error = new Error("Error updating project", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    loading.value = false;
  }

  try {
    await addDoc(collection<Log>(db, logsFullPath(props.accountId)), {
      name: isArchived ? "account.reactivated" : "account.archived",
      createdAt: Timestamp.now(),
    });
  } catch (cause) {
    const error = new Error("Error adding log entry", { cause });
    console.error(error);
    captureException(error);
  }
};

const save = async () => {
  if (!accountName.value) return;

  if (
    user.isKind("admin") &&
    limitTransfers.value === "threshold" &&
    (transferAmountThreshold.value === null || transferAmountThreshold.value <= 0)
  ) {
    showErrorToast(t("errors.amountMustBePositive"));
    return;
  }

  if (assignedSupervisorsMode.value === "selected" && !selectedSupervisorIds.value.length) {
    showErrorToast(t("collaborators.selectAtLeastOne"));
    return;
  }

  if (contractType.value === "signatureApi" && !contractTemplateId.value) {
    showErrorToast(t("account.settings.missingContractTemplateId"));
    return;
  }

  if (!validateContractCustomFileInput()) {
    loading.value = false;
    return;
  }

  loading.value = true;

  try {
    console.log("Saving project settings...");
    await saveGroup({
      id: groupId.value,
      name: newGroupName.value,
      accountId: props.accountId,
    });

    const accountRef = doc<Account>(db, `accounts/${props.accountId}`);

    let transferAmountThresholdUpdate;
    if (user.isKind("admin")) {
      switch (limitTransfers.value) {
        case "unlimited":
          transferAmountThresholdUpdate = null;
          break;
        case "all":
          transferAmountThresholdUpdate = 0;
          break;
        case "threshold":
          transferAmountThresholdUpdate = transferAmountThreshold.value ?? 0;
          break;
      }
    }

    let assignedSupervisors: AssignedSupervisors = {
      mode: "selected",
      uids: selectedSupervisorIds.value,
    };
    if (assignedSupervisorsMode.value !== "selected")
      assignedSupervisors = { mode: assignedSupervisorsMode.value };

    const updateData: Partial<
      Pick<
        Account,
        | "name"
        | "assignedSupervisors"
        | "externalApprovers"
        | "operatorLanguage"
        | "contractType"
        | "contractTemplateId"
        | "proAccountPrice"
        | "notifications"
        | "canUseSwanCards"
        | "transferAmountThreshold"
      >
    > = {
      name: accountName.value,
      assignedSupervisors,
      externalApprovers: externalApproverAssignments.value,
      ...(transferAmountThresholdUpdate !== undefined && {
        transferAmountThreshold: transferAmountThresholdUpdate,
      }),
      ...(newOperatorLanguage.value && { operatorLanguage: newOperatorLanguage.value }),
      ...(account.value &&
      getProviderType(account.value.accountType) === "proAccount" &&
      user.isKind("admin")
        ? {
            contractType: contractType.value,
            ...(contractTemplateId.value ? { contractTemplateId: contractTemplateId.value } : {}),
            ...(editableProAccountPrice.value
              ? { proAccountPrice: editableProAccountPrice.value }
              : {}),
          }
        : {}),
      ...(account.value && getProviderType(account.value.accountType) === "proAccount"
        ? { notifications: notifications.value }
        : {}),
      ...(account.value?.accountType === "swan" ? { canUseSwanCards: canUseSwanCards.value } : {}),
    };

    if (contractCustomFileInput.value) {
      await uploadContractCustom(contractCustomFileInput.value, {
        contentDisposition: `inline; filename="${contractCustomFileInput.value.name}"`,
        customMetadata: {
          name: contractCustomFileInput.value.name,
        },
      });
      updateData.contractType = "custom";
    }

    await updateDoc(accountRef, updateData);

    newGroupName.value = null;
    contractCustomFileInput.value = null;

    showSuccessToast(t("account.settings.saveSuccess"));
  } catch (cause) {
    const error = new Error("Error saving project", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(t("account.settings.saveError"));
  } finally {
    loading.value = false;
  }
};

const sendLink = async ({ id }: { id: string }) => {
  try {
    console.log("Sending invite link...");
    await call("sendOperatorConnectLinkEmail")({ accountId: props.accountId, contactId: id });
    showSuccessToast(t("account.settings.linkSentSuccess"));
  } catch (cause) {
    const error = new Error("Error resending invite", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};

const handleActiveNotifications = async (contact: ContactWithId) => {
  try {
    console.log("Activating notifications...");
    const contactRef = doc(contactsSource.value, contact.id);
    const updateData: Partial<Contact> = {
      notificationsDisabled: false,
    };
    await updateDoc(contactRef, updateData);
    showSuccessToast();
  } catch (cause) {
    const error = new Error("Error activating notifications", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};

const openAddDialog = () => {
  editingContact.value = null;
  showContactDialog.value = true;
};

const handleContactAdd = async (email: string, phone: string) => {
  contactActionLoading.value = true;

  try {
    console.log("Adding contact...");
    await addDoc(contactsSource.value, {
      email: email.toLowerCase(),
      phone: phone.trim(),
      createdAt: serverTimestamp(),
    });

    showSuccessToast(t("account.settings.contactAdded"));
    showContactDialog.value = false;
    editingContact.value = null;
  } catch (cause) {
    const error = new Error("Error adding contact", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(t("account.settings.contactAddError"));
  } finally {
    contactActionLoading.value = false;
  }
};

const removeContact = async ({ id }: { id: string }) => {
  try {
    console.log("Removing contact...");
    await deleteDoc(doc(contactsSource.value, id));
  } catch (cause) {
    const error = new Error("Error removing contact", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};

const editContact = (contact: ContactWithId) => {
  editingContact.value = contact;
  showContactDialog.value = true;
};

const handleContactUpdate = async (contact: ContactWithId, email: string, phone: string) => {
  if (!contact || !("id" in contact)) return;

  contactActionLoading.value = true;

  try {
    console.log("Updating contact...");
    const contactRef = doc(contactsSource.value, contact.id);

    const updateData: Partial<Contact> = {
      email: email.toLowerCase(),
      phone: phone.trim(),
    };

    await updateDoc(contactRef, updateData);

    showSuccessToast(t("account.settings.contactUpdated"));
    showContactDialog.value = false;
    editingContact.value = null;
  } catch (cause) {
    const error = new Error("Error updating contact", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(t("account.settings.contactUpdateError"));
  } finally {
    contactActionLoading.value = false;
  }
};

const openEmailHistory = (contact: Contact) => {
  const paramsString = "f%5Brange%5D=all";
  const searchParams = new URLSearchParams(paramsString);
  searchParams.append("f[query]", contact.email);

  window.open(
    `https://account.postmarkapp.com/servers/13106558/streams/outbound/events?${searchParams.toString()}`,
    "_blank",
  );
};

const handlePowensSync = async () => {
  try {
    console.log("Syncing Powens connection...");
    syncingPowens.value = true;
    await call("syncPowensConnection")({ accountId: props.accountId });
    showSuccessToast(t("account.settings.powensAdmin.syncSuccess"));
  } catch (cause) {
    const error = new Error("Error requesting Powens sync", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(t("account.settings.powensAdmin.syncError"));
  } finally {
    syncingPowens.value = false;
  }
};

const deleteAccountHandler = async () => {
  try {
    const confirmed = window.confirm(t("account.settings.deleteAccountConfirmation"));
    if (!confirmed) {
      return;
    }
    console.log("Deleting project...");
    deletingAccount.value = true;
    await call("deleteAccount")({ accountId: props.accountId });
    showSuccessToast();
    void router.push({ name: "Accounts" });
  } catch (cause) {
    const error = new Error("Error deleting project", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    deletingAccount.value = false;
  }
};

const syncTreezorAccount = async () => {
  syncingTreezor.value = true;
  try {
    console.log("Syncing Treezor project...");
    await call("syncTreezorAccount")({
      accountId: props.accountId,
    });
    showSuccessToast();
  } catch (cause) {
    const error = new Error("Error syncing Treezor project", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    syncingTreezor.value = false;
  }
};

const simulateTreezorKYBFlow = async () => {
  simulatingTreezorKYBFlow.value = true;
  try {
    console.log("Simulating Treezor KYB flow...");
    await call("simulateTreezorKYBFlow")({
      accountId: props.accountId,
    });
    showSuccessToast();
  } catch (cause) {
    const error = new Error("Error simulating Treezor KYB flow", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    simulatingTreezorKYBFlow.value = false;
  }
};

const simulateTreezorCreditWallet = async () => {
  const amountInput = window.prompt(
    t("account.settings.simulateTreezorCreditWalletAmountPrompt"),
    "100",
  );
  if (!amountInput) return;

  const amount = parseFloat(amountInput);
  if (isNaN(amount) || amount <= 0) {
    showErrorToast();
    return;
  }

  creditingTreezorWallet.value = true;
  try {
    console.log("Simulating Treezor credit wallet...");
    await call("simulateTreezorCreditWallet")({
      accountId: props.accountId,
      amount,
    });
    showSuccessToast();
  } catch (cause) {
    const error = new Error("Error simulating Treezor credit wallet", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    creditingTreezorWallet.value = false;
  }
};
</script>
